import type { ComparisonContent } from './types';

/**
 * Competitor comparison pages.
 *
 * Every competitor statement below was read from the competitor's own website
 * on the date recorded in `verifiedOn`. Nothing is inferred from memory: if a
 * fact was not on their page, it is not in the table. Prices are only quoted
 * where the competitor publishes them.
 */
export const comparisons: ComparisonContent[] = [
  {
    slug: 'jh-heatsup-vs-bromic',
    nav: 'vs Bromic',
    title: 'JH Heatsup vs Bromic: Factory or Brand?',
    description:
      'A side-by-side look at JH Heatsup and Bromic infrared outdoor heaters: product range, output, controls, certification, supply model and total landed cost.',
    h1: 'JH Heatsup vs Bromic: manufacturer or premium brand?',
    lede:
      'This is not a contest of who makes better heaters. It is a question about where you sit in the supply chain — and what that costs you per unit.',
    answer:
      'Bromic is a premium branded infrared heating company selling through distributors and dealers, with gas and electric collections and a published design service. JH Heatsup is the factory: 1.0–4.0 kW far-infrared heaters, IPX4, CE/CB/SAA/EMC/RoHS/SASO, OEM and ODM programmes for importers and project contractors. Choose Bromic for brand-led specification with local stock; choose JH Heatsup for private label, project volumes and direct factory pricing.',
    competitor: 'Bromic Heating',
    competitorSite: 'https://www.bromic.com/',
    verifiedOn: 'November 2026',
    rows: [
      {
        topic: 'Business model',
        ours: 'Manufacturer in Fujian, China (founded 2006); exports to 80+ countries; OEM/ODM programmes',
        theirs: 'Global brand selling through distributor and dealer networks across North America, Europe and Asia-Pacific',
      },
      {
        topic: 'Range',
        ours: 'Far-infrared wall panels 1.0–4.0 kW, ceiling units, portable carbon-fibre and halogen heaters',
        theirs: 'Eclipse (electric, seven mounting options including freestanding), Platinum (gas and electric), Tungsten (gas and electric), Marine for coastal and superyacht use',
      },
      {
        topic: 'Fuel options',
        ours: 'Electric only',
        theirs: 'Electric and gas',
      },
      {
        topic: 'Controls',
        ours: 'Wall switch, remote, Tuya WiFi with LCD remote (13W series)',
        theirs: 'Branded control ecosystem; specifics vary by collection — confirm with a Bromic dealer',
      },
      {
        topic: 'Certification',
        ours: 'CE, CB, SAA, EMC, RoHS, SASO, CCC, ISO 9001:2015 (documents supplied with quotation)',
        theirs: 'Market-specific certification handled through regional distribution; confirm for your destination',
      },
      {
        topic: 'Design support',
        ours: 'Layout and load schedule prepared by our project team from a plan or drawing',
        theirs: 'Complimentary outdoor heating design service advertised on their site',
      },
      {
        topic: 'Customisation',
        ours: 'Private label, packaging, voltage and plug configuration, controller options, OEM/ODM development',
        theirs: 'Branded catalogue configurations; bespoke manufacturer-level customisation is not the model',
      },
      {
        topic: 'Pricing basis',
        ours: 'Factory quotation, FOB or CIF, scaled by volume and configuration',
        theirs: 'Brand retail through dealers; list prices not published on the pages reviewed',
      },
    ],
    oursWins: [
      'Private label and OEM projects: your brand, your packaging, your voltage and plug configuration.',
      'Project volumes where the per-unit factory price matters more than brand recognition on the specification sheet.',
      'Direct engineering contact: the people who set the element and wattage are the people answering your email.',
      'Single-source range from 1.0 kW wall panels to 4.0 kW three-phase units, plus portable models for temporary capacity.',
    ],
    theirsWins: [
      'Gas heating options — our range is electric only, and a gas requirement is a genuine reason to choose Bromic.',
      'Local stock and dealer support in North America, Europe and Australia, which shortens lead time for small replacement orders.',
      'Brand recognition on a hospitality specification where the operator or franchise standard names a brand.',
      'Marine and superyacht applications with dedicated product engineering.',
    ],
    costModel: [
      'Compare landed cost, not unit price: freight, duty, certification, and the cost of holding stock all sit between the factory quote and the installed heater.',
      'A branded heater bought through a dealer carries distributor margin; a factory programme replaces that margin with your own import and stock function.',
      'For replacement elements and spare parts, ask both suppliers for the part price and lead time before you commit — this is where a five-year cost difference usually appears.',
      'Small orders favour local stock; container-scale orders favour the factory, which is the point at which OEM programmes become economic.',
    ],
    faqs: [
      {
        q: 'Is JH Heatsup a competitor to Bromic?',
        a: 'We compete in the same category but from a different position: Bromic is a branded manufacturer selling through distribution, and we are a contract manufacturer supplying importers, distributors and project contractors, including under their own brand. Some of our customers sell against Bromic.',
      },
      {
        q: 'Can JH Heatsup supply gas patio heaters like Bromic?',
        a: 'No. Our range is electric far-infrared. If a project requires gas, Bromic is a legitimate choice and we will say so rather than propose an electric substitute for a gas specification.',
      },
      {
        q: 'How does certification compare?',
        a: 'Our units carry CE, CB, SAA, EMC, RoHS, SASO and CCC, with ISO 9001:2015 for the factory, and documents are supplied with the quotation. Bromic handles market certification through its regional distribution — confirm the applicable certificate for your destination with their local dealer.',
      },
      {
        q: 'What is the minimum order for an OEM programme?',
        a: 'It depends on the model and the level of customisation — a private-label carton is a different project from a new controller or a voltage variant. Send the model and the change you need and we will quote the minimum together with the tooling or artwork cost.',
      },
      {
        q: 'Which supplier should a restaurant chain choose?',
        a: 'If the chain specifies a brand for guest recognition or franchise compliance, buy the brand. If the chain controls its own specification and buys by container for multiple sites, a factory programme with consistent spares is usually the lower total cost.',
      },
    ],
    sources: [
      { label: 'Bromic Heating — company and collection overview (accessed November 2026)', url: 'https://www.bromic.com/' },
      { label: 'JH Heatsup product specifications — 13 and 22 series', url: 'https://www.jhheatsup.com/infrared-heaters' },
      { label: 'JH Heatsup certifications and factory facts', url: 'https://www.jhheatsup.com/about' },
    ],
  },
  {
    slug: 'jh-heatsup-vs-infratech',
    nav: 'vs Infratech',
    title: 'JH Heatsup vs Infratech Heaters',
    description:
      'Infratech vs JH Heatsup for patio heating projects: output ranges, mounting heights, controls, voltage, certification and supply model compared from published specs.',
    h1: 'JH Heatsup vs Infratech: output range versus supply flexibility',
    lede:
      'Infratech builds high-output heaters for the North American market. We build export-voltage heaters for importers who need their own brand on the carton.',
    answer:
      'Infratech publishes 1,500–6,000 W heaters with 7–12 ft mounting heights and an extensive control range, made in the USA. JH Heatsup supplies 1.0–4.0 kW far-infrared heaters at 220–240 V (380 V for 4.0 kW), IPX4, with CE/CB/SAA/EMC/RoHS/SASO certification and OEM/ODM programmes. For a US-specified project needing 6 kW units, Infratech fits; for export markets and private label, JH Heatsup does.',
    competitor: 'Infratech',
    competitorSite: 'https://www.infratech.com/',
    verifiedOn: 'November 2026',
    rows: [
      {
        topic: 'Manufacturing base',
        ours: 'Fujian, China — 4,500 m² factory, 150+ staff, 10,000+ units per month capacity',
        theirs: 'Made in USA manufacturing, stated on their site',
      },
      {
        topic: 'Output range',
        ours: '1.0 / 1.8 / 2.4 / 3.2 / 4.0 kW wall panels; 1.6 / 2.2 / 3.2 kW for the 22B',
        theirs: '1,500–6,000 W for C/CD and W/WD series; 1,600 / 2,400 / 3,000 / 4,000 W for SL series',
      },
      {
        topic: 'Mounting height',
        ours: '1.8–2.5 m recommended',
        theirs: '7–12 ft (≈2.1–3.7 m) for C/CD and W/WD; up to 10 ft (≈3.0 m) for SL',
      },
      {
        topic: 'Formats',
        ours: 'Wall panel, ceiling, freestanding portable',
        theirs: 'C-shaped surface mount, W-shaped flush mount, slim SL surface mount, decorative Motif fascias',
      },
      {
        topic: 'Controls',
        ours: 'Switch, remote, Tuya WiFi (13W); BMS via contactor zoning',
        theirs: 'Extensive control ecosystem including smart packages, timers, input regulators and custom controls',
      },
      {
        topic: 'Supply voltage',
        ours: '220–240 V 50/60 Hz; 380 V for the 4.0 kW model',
        theirs: 'North American market voltages; confirm the exact configuration for your project',
      },
      {
        topic: 'Certification',
        ours: 'CE, CB, SAA, EMC, RoHS, SASO, CCC, ISO 9001:2015',
        theirs: 'US-market listings; confirm the applicable mark for your jurisdiction',
      },
      {
        topic: 'Supply model',
        ours: 'Factory direct, OEM/ODM, private label, export documentation',
        theirs: 'US manufacturer selling through trade programmes, dealers and specifier channels',
      },
    ],
    oursWins: [
      'Export voltages as standard, which matters in the EU, Middle East, Southeast Asia and Australia.',
      'Private label and packaging control for distributors building their own brand.',
      'A single 4,000 W unit at 380 V for sites that cannot distribute many single-phase circuits.',
      'Direct factory contact for spare elements and project-specific documentation.',
    ],
    theirsWins: [
      'Outputs above 4 kW — up to 6,000 W per unit — for very large or very exposed spaces.',
      'Mounting heights up to 12 ft for architectural installations with high fixing points.',
      'Flush-mount and decorative fascia options for design-led residential projects.',
      'A deep control catalogue for complex multi-zone commercial systems.',
    ],
    costModel: [
      'High-output US-made units reduce the number of fixtures on a large project but carry a higher unit cost and, outside North America, freight and duty.',
      'Lower-output export units need more fixtures per zone; the trade-off is easier circuit planning and cheaper replacement stock.',
      'Ask for the element price and availability for both options — a heater is a consumable over a ten-year horizon.',
      'For distributor programmes, factor in private-label artwork and carton tooling, which is a one-off cost amortised over the first order.',
    ],
    faqs: [
      {
        q: 'Does JH Heatsup make heaters above 4 kW?',
        a: 'No. Our highest single-unit output is 4.0 kW, which requires a 380 V supply. Where a project needs 6 kW per fixture, Infratech publishes units in that range and we would point you there rather than propose two of ours as an equivalent.',
      },
      {
        q: 'Are the mounting heights different?',
        a: 'Yes. We recommend 1.8–2.5 m, while Infratech publishes 7–12 ft (about 2.1–3.7 m) for their C/CD and W/WD series. Higher mounting widens the radiant footprint but reduces intensity at occupant level, so the correct height depends on the space.',
      },
      {
        q: 'Can JH heaters be used in the United States?',
        a: 'Our standard supply is 220–240 V 50/60 Hz, which does not match typical North American residential circuits. For US projects, confirm the voltage and the required listing with your importer before ordering — we will tell you plainly whether our configuration fits.',
      },
      {
        q: 'Which has better controls for a large commercial system?',
        a: 'Infratech publishes a broader dedicated control catalogue, including smart packages and input regulators. Our approach is simpler: local switching or WiFi on the 13W series, with contactor-based zoning on the distribution board for BMS integration.',
      },
      {
        q: 'Can I buy Infratech-compatible spare parts from JH Heatsup?',
        a: 'No. Elements and controllers are not interchangeable between manufacturers. Specify one system per installation; mixing parts across brands is how maintenance problems start.',
      },
    ],
    sources: [
      { label: 'Infratech — heater series, outputs and mounting heights (accessed November 2026)', url: 'https://www.infratech.com/heaters/' },
      { label: 'JH Heatsup product specifications', url: 'https://www.jhheatsup.com/infrared-heaters' },
      { label: 'JH Heatsup factory and certification details', url: 'https://www.jhheatsup.com/about' },
    ],
  },
  {
    slug: 'jh-heatsup-vs-herschel',
    nav: 'vs Herschel',
    title: 'JH Heatsup vs Herschel Infrared',
    description:
      'Herschel vs JH Heatsup: UK-made low-wattage panels for homes versus export far-infrared heaters for terraces and commercial projects. Specs, warranty and pricing models.',
    h1: 'JH Heatsup vs Herschel: home panels or terrace heat?',
    lede:
      'These two ranges rarely compete for the same order. Knowing which is which saves a specification mistake.',
    answer:
      'Herschel is a UK manufacturer of low-wattage infrared panels for homes, churches and commercial interiors, publishing 215–1,250 W panels with a 10-year warranty on its Comfort range. JH Heatsup builds 1.0–4.0 kW far-infrared heaters for semi-outdoor terraces, workshops and hospitality projects, with IPX4 protection and OEM/ODM supply. Indoor room heating points to Herschel; outdoor and semi-outdoor coverage points to us.',
    competitor: 'Herschel Infrared',
    competitorSite: 'https://www.herschel-infrared.co.uk/',
    verifiedOn: 'November 2026',
    rows: [
      {
        topic: 'Primary application',
        ours: 'Semi-outdoor and commercial: restaurant terraces, hotel courtyards, workshops, halls',
        theirs: 'Indoor residential and commercial interiors, churches and heritage buildings, plus a designer patio range',
      },
      {
        topic: 'Output range',
        ours: '1.0–4.0 kW per unit',
        theirs: 'Comfort panels 215–1,250 W across 8 models; Select XLS 300–1,100 W; Krystal 600–1,000 W',
      },
      {
        topic: 'Ingress protection',
        ours: 'IPX4 on the infrared and portable ranges',
        theirs: 'Panel products are primarily indoor; confirm the rating of any outdoor model with Herschel',
      },
      {
        topic: 'Warranty',
        ours: 'See our warranty policy for current residential and industrial terms',
        theirs: '10 years on Comfort panels, 5 years on Select XLS, as published on their site',
      },
      {
        topic: 'Manufacturing',
        ours: 'Fujian, China; 4,500 m² factory; 10,000+ units per month',
        theirs: 'UK production facility, using British steel and a German heating element per their site',
      },
      {
        topic: 'Certification',
        ours: 'CE, CB, SAA, EMC, RoHS, SASO, CCC, ISO 9001:2015',
        theirs: 'UK/EU market compliance; Comfort tested to IEC60675 for radiant efficiency per their site',
      },
      {
        topic: 'Pricing model',
        ours: 'Factory quotation by volume and configuration',
        theirs: 'Published retail pricing, e.g. Comfort from £309 and Select XLS from £238 at the time of review',
      },
      {
        topic: 'Customisation',
        ours: 'OEM/ODM, private label, voltage and plug variants',
        theirs: 'Branded product range with colour options and made-to-order picture panels',
      },
    ],
    oursWins: [
      'Outdoor and semi-outdoor coverage: 1.0–4.0 kW per unit with IPX4, where a 1 kW indoor panel cannot hold a terrace.',
      'Project volumes for hospitality and industrial buyers who need consistent spares across many sites.',
      'Private label and export voltage configuration.',
      'Higher output per fixture, which reduces the number of units and fixing points in a large space.',
    ],
    theirsWins: [
      'Indoor room heating where a slim, low-wattage panel is the right product and aesthetics dominate.',
      'A published 10-year warranty on their main panel range — a strong offer for residential retail.',
      'UK-based manufacturing and local stock for British trade buyers.',
      'Bathroom, mirror and towel formats that our range does not cover.',
    ],
    costModel: [
      'Compare like with like: a 1 kW indoor panel and a 3.2 kW terrace heater are different products with different jobs, so a per-watt comparison misleads.',
      'For terraces, calculate cost per covered m², not cost per unit — a low-wattage panel usually needs more units to reach the same coverage.',
      'Warranty length matters more in residential retail than in commercial projects, where spare-part availability and swap-out speed dominate.',
      'Retail pricing includes VAT and margin; factory pricing excludes freight, duty and certification, so build the landed cost before comparing.',
    ],
    faqs: [
      {
        q: 'Can I use Herschel-style low-wattage panels on a patio?',
        a: 'A 215–1,250 W indoor panel does not have the output or the ingress protection for an exposed terrace. Outdoor heating needs higher output per fixture and an IP rating appropriate to the installation; that is the segment our 1.0–4.0 kW IPX4 range is built for.',
      },
      {
        q: 'Which has the longer warranty?',
        a: 'Herschel publishes 10 years on its Comfort panels and 5 years on Select XLS. Our terms are set out in our warranty policy and differ between residential and industrial use — check the current document rather than assuming parity.',
      },
      {
        q: 'Are JH Heatsup panels made in the UK?',
        a: 'No. Our factory is in Fujian, China, with ISO 9001:2015 certification and CE, CB, SAA, EMC, RoHS and SASO documentation. Herschel manufactures in the UK, which is a genuine advantage for buyers who want domestic production.',
      },
      {
        q: 'Can JH Heatsup supply indoor panels like Herschel?',
        a: 'Our range is oriented to semi-outdoor and commercial applications. For pure indoor room heating at low wattage, a dedicated panel range is the better fit — we will say so rather than stretch a terrace heater into a living room.',
      },
      {
        q: 'Do you sell to homeowners?',
        a: 'We sell to distributors, contractors and project buyers, typically by the pallet or container. For a single residential heater, the brand retail channel is usually the faster route.',
      },
    ],
    sources: [
      { label: 'Herschel Infrared — range, outputs, warranty and retail pricing (accessed November 2026)', url: 'https://www.herschel-infrared.co.uk/' },
      { label: 'JH Heatsup warranty policy', url: 'https://www.jhheatsup.com/warranty-policy' },
      { label: 'JH Heatsup product specifications', url: 'https://www.jhheatsup.com/infrared-heaters' },
    ],
  },
];

export const COMPARISON_SLUGS: string[] = comparisons.map((item) => item.slug);
export const COMPARISON_PATHS: string[] = COMPARISON_SLUGS.map((slug) => `/compare/${slug}`);

export function getComparison(slug: string | undefined): ComparisonContent | undefined {
  return comparisons.find((item) => item.slug === slug);
}
